import { useEffect, useRef, useState } from 'react';

import { DashedTag } from '@/components/chariot-ui';
import { Modal } from '@/components/Modal';
import { useAssetTags } from '@/hooks/useAssetTags';
import { useAxios } from '@/hooks/useAxios';
import { Asset, Risk, Seed, Technology } from '@/types';

/**
 * RemoveTag Modal Component
 *
 * Reusable UI component for removing tags from entities (assets, technologies, etc.).
 * Displays common tags across all selected entities and allows user to select
 * one or more tags for removal.
 *
 * Features:
 * - Auto-fetches common tags across selected entities
 * - Multi-tag selection interface
 * - Auto-selects when only one tag available
 * - Pre-select via initialTag prop
 * - Select all / Deselect all controls
 * - Visual feedback showing selected tags
 * - Keyboard navigation (Enter/Space to toggle)
 * - ARIA listbox semantics
 * - Loading states while fetching
 * - Graceful handling of no common tags
 * - Generic support for different entity types (asset, technology, etc.)
 *
 * User Experience:
 * - Click a tag to toggle selection
 * - Multiple tags can be selected simultaneously
 * - Button shows count when multiple tags selected
 * - Tags are removed immediately when form is submitted
 *
 * @example
 * ```tsx
 * <RemoveTag
 *   open={open}
 *   onClose={onClose}
 *   disabled={false}
 *   onSubmit={(tag) => handleRemoveTag(tag)}
 *   assetKeys={['#asset#key1', '#asset#key2']}
 *   label="asset"  // or "technology"
 *   initialTag="my-tag"  // optional: pre-select a tag
 * />
 * ```
 */
export const RemoveTag = ({
  open,
  onClose,
  disabled,
  onSubmit,
  assetKeys,
  label = 'asset',
  initialTag,
}: {
  open: boolean;
  onClose: () => void;
  disabled: boolean;
  onSubmit: (tag: string) => void;
  assetKeys: string[];
  label?: 'asset' | 'technology' | 'seed' | 'vulnerability';
  initialTag?: string;
}) => {
  const axios = useAxios();
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
  const initialSelectionApplied = useRef(false);

  // ✅ NEW: Use useAssetTags hook for single asset case
  const singleAssetKey =
    label === 'asset' && assetKeys.length === 1 ? assetKeys[0] : undefined;
  const { tags: hookTags, isLoading: hookLoading } =
    useAssetTags(singleAssetKey);

  // Keep existing pattern for multiple assets or non-asset entity types
  const [manualTags, setManualTags] = useState<string[]>([]);
  const [manualLoading, setManualLoading] = useState(false);

  // Use hook data for single asset, manual fetch for multiple/non-asset
  const usingSingleAssetHook = !!singleAssetKey;
  const commonTags = usingSingleAssetHook ? hookTags : manualTags;
  const loading = usingSingleAssetHook ? hookLoading : manualLoading;

  const toggleTag = (tag: string) => {
    setSelectedTags(prev => {
      const next = new Set(prev);
      if (next.has(tag)) {
        next.delete(tag);
      } else {
        next.add(tag);
      }
      return next;
    });
  };

  // Fetch tags for multiple assets or non-asset entities (existing pattern)
  useEffect(() => {
    // Skip if using single asset hook
    if (usingSingleAssetHook) {
      return;
    }

    if (!open || assetKeys.length === 0) {
      // Reset state when modal closes
      setManualTags([]);
      setSelectedTags(new Set());
      initialSelectionApplied.current = false;
      return;
    }

    const fetchCommonTags = async () => {
      setManualLoading(true);
      try {
        // Fetch all entities in parallel
        const promises = assetKeys.map(key =>
          axios.get(`/my`, {
            params: {
              ...(label !== 'seed' ? { label: label } : {}),
              key: key,
              exact: true,
            },
          })
        );
        const responses = await Promise.all(promises);

        // Extract tags from each entity with proper type handling
        // Response structure: { assets: [...] } or { technologies: [...] } or { seeds: [...] } or { risks: [...] }
        const allTags = responses.map(res => {
          if (label === 'asset') {
            return (res.data.assets?.[0] as Asset)?.tags || [];
          } else if (label === 'technology') {
            return (res.data.technologies?.[0] as Technology)?.tags || [];
          } else if (label === 'vulnerability') {
            return (res.data.risks?.[0] as Risk)?.tags || [];
          } else {
            // Seeds are stored as assets/webapplications — check all possible response keys
            const items =
              res.data.assets || res.data.webapplications || res.data.seeds;
            const seedData = Array.isArray(items)
              ? items[0]
              : items
                ? Object.values(items)[0]
                : undefined;
            return (seedData as Seed)?.tags || [];
          }
        });

        // Collect all unique tags across all selected entities
        const uniqueTags = Array.from(new Set(allTags.flat())).sort();

        setManualTags(uniqueTags);
        setSelectedTags(new Set()); // Start with nothing selected
      } catch (error) {
        console.error('[RemoveTag] Failed to fetch tags:', error);
        setManualTags([]);
      } finally {
        setManualLoading(false);
      }
    };

    fetchCommonTags();
  }, [open, assetKeys, axios, label, usingSingleAssetHook]);

  // Auto-select when only one tag is available
  useEffect(() => {
    if (open && !loading && commonTags.length === 1) {
      setSelectedTags(prev =>
        prev.size === 0 ? new Set([commonTags[0]]) : prev
      );
    }
  }, [open, loading, commonTags]);

  // Pre-select from context when initialTag is provided
  useEffect(() => {
    if (
      open &&
      initialTag &&
      commonTags.includes(initialTag) &&
      !initialSelectionApplied.current
    ) {
      setSelectedTags(new Set([initialTag]));
      initialSelectionApplied.current = true;
    }
  }, [open, initialTag, commonTags]);

  function reset() {
    setSelectedTags(new Set());
    onClose();
  }

  // Handle case where no tags exist on any entity
  const entityName =
    label === 'asset'
      ? 'assets'
      : label === 'seed'
        ? 'seeds'
        : label === 'vulnerability'
          ? 'vulnerabilities'
          : 'technologies';

  return (
    <Modal
      title="Remove Tag"
      open={open}
      onClose={onClose}
      footer={
        commonTags.length === 0 && !loading
          ? {
              text: 'Close',
              onClick: onClose,
            }
          : {
              text:
                selectedTags.size > 1
                  ? `Remove ${selectedTags.size} Tags`
                  : 'Remove Tag',
              form: 'remove-tag-form',
              disabled: disabled || selectedTags.size === 0 || loading,
            }
      }
    >
      {commonTags.length > 0 && !loading && (
        <p className="text-chariot-primary mb-4">
          Click a tag below to select it for removal:
        </p>
      )}
      {selectedTags.size > 0 && (
        <p className="text-chariot-text-secondary mb-4 text-sm">
          Selected:{' '}
          <span className="font-semibold">
            {Array.from(selectedTags).join(', ')}
          </span>
        </p>
      )}
      {commonTags.length > 0 && !loading && (
        <p className="text-chariot-text-secondary mb-4 text-sm">
          The tag will only be removed from {entityName} that currently have it.
        </p>
      )}

      <form
        id="remove-tag-form"
        className="space-y-4"
        onSubmit={async event => {
          event.preventDefault();
          if (selectedTags.size > 0) {
            for (const tag of selectedTags) {
              await onSubmit(tag);
            }
            reset();
          }
        }}
      >
        {loading ? (
          <p className="text-chariot-text-secondary">Loading tags...</p>
        ) : commonTags.length === 0 ? (
          <>
            <p className="text-chariot-text-secondary">
              No tags found on selected {entityName}.
            </p>
            <p className="text-chariot-text-secondary mt-2 text-sm">
              Add tags to {entityName} before attempting to remove them.
            </p>
          </>
        ) : (
          <>
            {commonTags.length > 1 && (
              <div className="mb-2 flex gap-3 text-xs">
                <button
                  type="button"
                  className="text-chariot-brand-primary hover:underline"
                  onClick={() => setSelectedTags(new Set(commonTags))}
                >
                  Select all
                </button>
                {selectedTags.size > 0 && (
                  <button
                    type="button"
                    className="text-chariot-text-secondary hover:underline"
                    onClick={() => setSelectedTags(new Set())}
                  >
                    Deselect all
                  </button>
                )}
              </div>
            )}
            <div
              role="listbox"
              aria-label="Tags available for removal"
              aria-multiselectable="true"
              className="flex flex-wrap gap-2"
            >
              {commonTags.map(tag => (
                <div
                  key={tag}
                  role="option"
                  aria-selected={selectedTags.has(tag)}
                  tabIndex={0}
                  onClick={() => toggleTag(tag)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleTag(tag);
                    }
                  }}
                  className="focus-visible:ring-chariot-brand-primary rounded-full focus:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-1"
                >
                  <DashedTag
                    label={tag}
                    size="sm"
                    active={selectedTags.has(tag)}
                    className="hover:border-chariot-brand-primary/50 hover:bg-chariot-background-secondary/30 pointer-events-none cursor-pointer transition-all duration-200"
                    data-testid={`tag-option-${tag}`}
                  />
                </div>
              ))}
            </div>
          </>
        )}
      </form>
    </Modal>
  );
};
